/**
 * Shared domain types.
 *
 * Everything under `functions/src/shared/` is pure TypeScript with no runtime
 * dependencies, imported by BOTH the Cloud Functions build and the React app
 * (which reaches it via a relative path). Keep it free of Node and DOM APIs.
 */

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'paused'
  | 'completed'
  | 'canceled'
  | 'failed';

export type RecipientStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'bounced'
  | 'complained'
  | 'suppressed';

export type SuppressionReason =
  | 'hard_bounce'
  | 'spam_complaint'
  | 'unsubscribe'
  | 'manual';

/** Counters denormalized onto the campaign so the list view needs one read. */
export interface CampaignStats {
  total: number;
  queued: number;
  sent: number;
  failed: number;
  bounced: number;
  complained: number;
  suppressed: number;
  opened: number;
  clicked: number;
}

export const EMPTY_STATS: CampaignStats = {
  total: 0,
  queued: 0,
  sent: 0,
  failed: 0,
  bounced: 0,
  complained: 0,
  suppressed: 0,
  opened: 0,
  clicked: 0,
};

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  /** Sanitized HTML body, may contain {{merge_field}} tokens. */
  html: string;
  /** Plaintext alternative, auto-generated from `html` but editable. */
  text: string;

  /** Owner — also the envelope identity. */
  ownerUid: string;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  /**
   * False when the owner's address is not on a Postmark-verified domain, in
   * which case the function sends from DEFAULT_FROM and sets Reply-To instead.
   */
  fromIsVerified: boolean;

  status: CampaignStatus;
  stats: CampaignStats;

  /** Pacing. Effective rate is min(sendsPerMinute, remaining daily budget). */
  sendsPerMinute: number;
  dailyCap: number;

  createdAt: unknown;
  updatedAt: unknown;
  startedAt?: unknown;
  completedAt?: unknown;
  lastError?: string;
}

export interface Recipient {
  id: string;
  email: string;
  /** Every CSV column for this row, used for {{merge_field}} substitution. */
  mergeData: Record<string, string>;
  status: RecipientStatus;
  attempts: number;
  messageId?: string;
  lastError?: string;
  /** Lease marker so a crashed run doesn't strand rows in `sending`. */
  leasedAt?: unknown;
  sentAt?: unknown;
  deliveredAt?: unknown;
  openedAt?: unknown;
  clickedAt?: unknown;
}

export interface Suppression {
  id: string;
  email: string;
  reason: SuppressionReason;
  detail?: string;
  campaignId?: string;
  createdAt: unknown;
}
